import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Book, FileText, Rocket, Settings, ChevronRight, 
  ChevronDown, Search, ExternalLink, Home, Users,
  Shield, BarChart3, Bell, CreditCard, HelpCircle,
  Sparkles, Briefcase, ArrowLeft
} from 'lucide-react';

// Documentation structure with role-based access
const DOCS_STRUCTURE = {
  'getting-started': {
    title: 'Getting Started',
    icon: Rocket,
    roles: ['all'], // Everyone can access
    sections: [
      { id: 'introduction', title: 'Introduction', anchor: '1-introduction' },
      { id: 'logging-in', title: 'Logging In', anchor: '21-logging-in' },
      { id: 'navigation', title: 'Navigation Overview', anchor: '22-navigation-overview' }
    ]
  },
  'user-roles': {
    title: 'User Roles & Permissions',
    icon: Users,
    roles: ['all'],
    sections: [
      { id: 'role-overview', title: 'Role Overview', anchor: '3-user-roles--personas' },
      { id: 'sysadmin', title: 'System Administrator', anchor: '31-system-administrator-sysadmin', roles: ['sysadmin'] },
      { id: 'program-admin', title: 'Program Administrator', anchor: '33-program-administrator-program_admin', roles: ['sysadmin', 'pmo_power', 'program_admin'] },
      { id: 'pmo', title: 'PMO Power User', anchor: '32-pmo-power-user-pmo_power', roles: ['sysadmin', 'pmo_power'] },
      { id: 'pm', title: 'Project Manager', anchor: '34-project-manager-pm', roles: ['all'] },
      { id: 'viewer', title: 'Viewer', anchor: '35-viewer-viewer', roles: ['all'] },
      { id: 'access-matrix', title: 'Access Matrix', anchor: '4-access-matrix', roles: ['sysadmin', 'program_admin'] }
    ]
  },
  'ai-scoping': {
    title: 'AI Scoping Assistant',
    icon: Sparkles,
    roles: ['all'],
    sections: [
      { id: 'ai-overview', title: 'Overview', anchor: 'ai-assistant-overview' },
      { id: 'acceptance-kpis', title: 'Acceptance & KPIs', anchor: 'acceptance-kpis' },
      { id: 'ai-suggestions', title: 'AI Suggestions', anchor: 'ai-suggestions' },
      { id: 'checkpoint-summary', title: 'Checkpoint Summary', anchor: 'checkpoint-summary' }
    ]
  },
  'consulting-mode': {
    title: 'Consulting Mode',
    icon: Briefcase,
    roles: ['sysadmin', 'pmo_power', 'program_admin', 'pm'],
    sections: [
      { id: 'proposal-builder', title: 'Proposal Builder', anchor: 'proposal-builder' },
      { id: 'discovery-phase', title: 'Discovery Phase', anchor: 'discovery-phase' },
      { id: 'scoping-phase', title: 'Scoping Phase', anchor: 'scoping-phase' },
      { id: 'estimation-phase', title: 'Estimation Phase', anchor: 'estimation-phase' },
      { id: 'proposal-export', title: 'Exporting Proposals', anchor: 'proposal-export' }
    ]
  },
  'dashboards': {
    title: 'Dashboard Guides',
    icon: BarChart3,
    roles: ['all'],
    sections: [
      { id: 'executive', title: 'Executive Dashboard', anchor: '4-executive-dashboard-guide', roles: ['sysadmin', 'pmo_power', 'program_admin', 'viewer'] },
      { id: 'pmo-dashboard', title: 'PMO Dashboard', anchor: '5-pmoportfolio-dashboard-guide', roles: ['sysadmin', 'pmo_power', 'program_admin'] },
      { id: 'pm-dashboard', title: 'Project Manager Dashboard', anchor: '6-project-manager-dashboard-guide', roles: ['all'] }
    ]
  },
  'data-management': {
    title: 'Data Management',
    icon: FileText,
    roles: ['sysadmin', 'pmo_power', 'program_admin', 'pm'],
    sections: [
      { id: 'upload-center', title: 'Upload Center', anchor: '71-upload-center' },
      { id: 'excel-template', title: 'Excel Template', anchor: '71-upload-center' },
      { id: 'version-history', title: 'Version History', anchor: '72-version-history' }
    ]
  },
  'notifications': {
    title: 'Daily Signal Notifications',
    icon: Bell,
    roles: ['all'],
    sections: [
      { id: 'overview', title: 'Overview', anchor: '81-overview' },
      { id: 'user-settings', title: 'User Settings', anchor: '82-user-settings' },
      { id: 'teams-integration', title: 'Microsoft Teams', anchor: '84-microsoft-teams-integration' }
    ]
  },
  'administration': {
    title: 'Administration',
    icon: Settings,
    roles: ['sysadmin', 'pmo_power', 'program_admin'],
    sections: [
      { id: 'user-management', title: 'User Management', anchor: '91-user-management' },
      { id: 'roles-permissions', title: 'Roles & Permissions', anchor: '92-roles--permissions' },
      { id: 'notification-admin', title: 'Notification Admin', anchor: '93-daily-signal-administration' },
      { id: 'trial-management', title: 'Trial Management', anchor: '94-trial-management', roles: ['sysadmin'] },
      { id: 'feature-flags', title: 'Feature Flags', anchor: 'feature-flags-admin', roles: ['sysadmin'] },
      { id: 'tier-management', title: 'Tier Management', anchor: 'tier-management', roles: ['sysadmin'] }
    ]
  },
  'billing': {
    title: 'Billing & Subscriptions',
    icon: CreditCard,
    roles: ['sysadmin'],
    sections: [
      { id: 'pricing-tiers', title: 'Pricing Tiers', anchor: '101-pricing-tiers' },
      { id: 'managing-tenants', title: 'Managing Tenants', anchor: '102-managing-tenants' },
      { id: 'feature-bundles', title: 'Feature Bundles', anchor: '103-feature-bundles' },
      { id: 'invoicing', title: 'Invoicing & Clients', anchor: 'invoicing-clients' }
    ]
  },
  'troubleshooting': {
    title: 'Help & FAQ',
    icon: HelpCircle,
    roles: ['all'],
    sections: [
      { id: 'common-issues', title: 'Common Issues', anchor: '111-common-issues' },
      { id: 'faq', title: 'FAQ', anchor: '112-faq' }
    ]
  }
};

// Quick start guides by role
const QUICK_START_GUIDES = {
  'sysadmin': { title: 'System Administrator', anchor: 'quick-start-system-administrator' },
  'pmo_power': { title: 'PMO User', anchor: 'quick-start-pmo-user' },
  'program_admin': { title: 'PMO User', anchor: 'quick-start-pmo-user' },
  'pm': { title: 'Project Manager', anchor: 'quick-start-project-manager' },
  'viewer': { title: 'Viewer', anchor: 'quick-start-viewer' }
};

const DocumentationPage = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [activeSection, setActiveSection] = useState('getting-started');
  const [expandedSections, setExpandedSections] = useState(['getting-started']);
  const [searchQuery, setSearchQuery] = useState('');
  const [docContent, setDocContent] = useState('');
  const [quickStartContent, setQuickStartContent] = useState('');
  const [adminGuideContent, setAdminGuideContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeDoc, setActiveDoc] = useState('user-manual'); // user-manual, quick-start, admin-guide

  // Get user's primary role
  const userRole = user?.roles?.[0]?.role_key || 'viewer';
  const isAdmin = hasRole('sysadmin', 'pmo_power');

  // Load documentation content
  useEffect(() => {
    const loadDocs = async () => {
      setLoading(true);
      try {
        // Load main user manual
        const userManualRes = await fetch('/docs/USER_MANUAL.md');
        if (userManualRes.ok) {
          const text = await userManualRes.text();
          setDocContent(text);
        }

        // Load quick start guides
        const quickStartRes = await fetch('/docs/QUICK_START_GUIDES.md');
        if (quickStartRes.ok) {
          const text = await quickStartRes.text();
          setQuickStartContent(text);
        }

        // Load admin guide (only for admins)
        if (isAdmin) {
          const adminRes = await fetch('/docs/ADMIN_CONFIG_GUIDE.md');
          if (adminRes.ok) {
            const text = await adminRes.text();
            setAdminGuideContent(text);
          }
        }
      } catch (error) {
        console.error('Error loading documentation:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDocs();
  }, [isAdmin]);

  // Check if user has access to a section
  const hasAccessToSection = (roles) => {
    if (roles.includes('all')) return true;
    return roles.some(role => hasRole(role));
  };

  // Filter docs structure based on user role
  const filteredDocs = Object.entries(DOCS_STRUCTURE).filter(([key, section]) => 
    hasAccessToSection(section.roles)
  );

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Get current content based on active doc type
  const getCurrentContent = () => {
    switch (activeDoc) {
      case 'quick-start':
        return quickStartContent;
      case 'admin-guide':
        return adminGuideContent;
      default:
        return docContent;
    }
  };

  // Search filter for content
  const filteredContent = searchQuery 
    ? getCurrentContent().split('\n').filter(line => 
        line.toLowerCase().includes(searchQuery.toLowerCase())
      ).join('\n')
    : getCurrentContent();

  // Custom components for markdown rendering
  const markdownComponents = {
    h1: ({ children }) => (
      <h1 className="text-3xl font-bold text-[var(--clr-text-primary)] mt-8 mb-4 pb-2 border-b border-[var(--clr-border-light)]">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-2xl font-semibold text-[var(--clr-text-primary)] mt-6 mb-3" id={children?.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-')}>
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl font-semibold text-[var(--clr-text-primary)] mt-5 mb-2">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-lg font-medium text-[var(--clr-text-primary)] mt-4 mb-2">
        {children}
      </h4>
    ),
    p: ({ children }) => (
      <p className="text-[var(--clr-text-secondary)] mb-4 leading-relaxed">
        {children}
      </p>
    ),
    ul: ({ children }) => (
      <ul className="list-disc list-inside mb-4 space-y-1 text-[var(--clr-text-secondary)]">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-inside mb-4 space-y-1 text-[var(--clr-text-secondary)]">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="text-[var(--clr-text-secondary)]">
        {children}
      </li>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto mb-4">
        <table className="min-w-full border border-[var(--clr-border-light)] rounded-lg overflow-hidden">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-[var(--clr-surface-subtle)]">
        {children}
      </thead>
    ),
    th: ({ children }) => (
      <th className="px-4 py-2 text-left text-sm font-semibold text-[var(--clr-text-primary)] border-b border-[var(--clr-border-light)]">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-4 py-2 text-sm text-[var(--clr-text-secondary)] border-b border-[var(--clr-border-light)]">
        {children}
      </td>
    ),
    code: ({ inline, children }) => (
      inline ? (
        <code className="px-1.5 py-0.5 bg-[var(--clr-surface-subtle)] text-[var(--clr-brand-primary)] rounded text-sm font-mono">
          {children}
        </code>
      ) : (
        <code className="block p-4 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto text-sm font-mono mb-4">
          {children}
        </code>
      )
    ),
    pre: ({ children }) => (
      <pre className="bg-gray-900 text-gray-100 rounded-lg overflow-x-auto mb-4">
        {children}
      </pre>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-[var(--clr-brand-primary)] pl-4 py-2 my-4 bg-[var(--clr-surface-subtle)] rounded-r-lg">
        {children}
      </blockquote>
    ),
    hr: () => (
      <hr className="my-8 border-t border-[var(--clr-border-light)]" />
    ),
    a: ({ href, children }) => (
      <a 
        href={href} 
        className="text-[var(--clr-brand-primary)] hover:underline inline-flex items-center gap-1"
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      >
        {children}
        {href?.startsWith('http') && <ExternalLink className="w-3 h-3" />}
      </a>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold text-[var(--clr-text-primary)]">
        {children}
      </strong>
    )
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--clr-surface-page)]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--clr-brand-primary)]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--clr-surface-page)]" data-testid="documentation-page">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-72 bg-[var(--clr-surface-card)] border-r border-[var(--clr-border-light)] min-h-screen sticky top-0 overflow-y-auto">
          <div className="p-6">
            {/* Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 mb-4 text-sm text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)] transition-colors group"
              data-testid="back-to-app-btn"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span>Back to ScopeOps</span>
            </button>

            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[var(--clr-brand-primary)] rounded-lg flex items-center justify-center">
                <Book className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-[var(--clr-text-primary)]">Documentation</h1>
                <p className="text-xs text-[var(--clr-text-muted)]">ScopeOps Help Center</p>
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--clr-text-muted)]" />
              <input
                type="text"
                placeholder="Search docs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[var(--clr-border-light)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
                data-testid="docs-search"
              />
            </div>

            {/* Doc Type Tabs */}
            <div className="flex gap-1 mb-6 p-1 bg-[var(--clr-surface-subtle)] rounded-lg">
              <button
                onClick={() => setActiveDoc('user-manual')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  activeDoc === 'user-manual' 
                    ? 'bg-white text-[var(--clr-brand-primary)] shadow-sm' 
                    : 'text-[var(--clr-text-muted)] hover:text-[var(--clr-text-primary)]'
                }`}
                data-testid="tab-user-manual"
              >
                Manual
              </button>
              <button
                onClick={() => setActiveDoc('quick-start')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  activeDoc === 'quick-start' 
                    ? 'bg-white text-[var(--clr-brand-primary)] shadow-sm' 
                    : 'text-[var(--clr-text-muted)] hover:text-[var(--clr-text-primary)]'
                }`}
                data-testid="tab-quick-start"
              >
                Quick Start
              </button>
              {isAdmin && (
                <button
                  onClick={() => setActiveDoc('admin-guide')}
                  className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    activeDoc === 'admin-guide' 
                      ? 'bg-white text-[var(--clr-brand-primary)] shadow-sm' 
                      : 'text-[var(--clr-text-muted)] hover:text-[var(--clr-text-primary)]'
                  }`}
                  data-testid="tab-admin-guide"
                >
                  Admin
                </button>
              )}
            </div>

            {/* Quick Start Card */}
            {activeDoc === 'user-manual' && QUICK_START_GUIDES[userRole] && (
              <div 
                className="mb-6 p-4 bg-gradient-to-r from-[var(--clr-brand-primary)] to-indigo-600 rounded-xl text-white cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setActiveDoc('quick-start')}
                data-testid="quick-start-card"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Rocket className="w-4 h-4" />
                  <span className="text-sm font-medium">Your Quick Start</span>
                </div>
                <p className="text-xs text-white/80">
                  {QUICK_START_GUIDES[userRole].title} Guide
                </p>
              </div>
            )}

            {/* Navigation */}
            <nav className="space-y-1">
              {filteredDocs.map(([key, section]) => {
                const Icon = section.icon;
                const isExpanded = expandedSections.includes(key);
                const filteredSubsections = section.sections.filter(sub => 
                  !sub.roles || hasAccessToSection(sub.roles)
                );

                return (
                  <div key={key}>
                    <button
                      onClick={() => {
                        toggleSection(key);
                        setActiveSection(key);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeSection === key
                          ? 'bg-[var(--clr-brand-primary)]/10 text-[var(--clr-brand-primary)]'
                          : 'text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-subtle)]'
                      }`}
                      data-testid={`nav-${key}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        <span>{section.title}</span>
                      </div>
                      {filteredSubsections.length > 0 && (
                        isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
                      )}
                    </button>

                    {isExpanded && filteredSubsections.length > 0 && (
                      <div className="ml-6 mt-1 space-y-1">
                        {filteredSubsections.map(sub => (
                          <a
                            key={sub.id}
                            href={`#${sub.anchor}`}
                            className="block px-3 py-1.5 text-sm text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)] transition-colors"
                          >
                            {sub.title}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>

          {/* Role Badge */}
          <div className="p-4 border-t border-[var(--clr-border-light)]">
            <div className="flex items-center gap-2 text-xs text-[var(--clr-text-muted)]">
              <Shield className="w-4 h-4" />
              <span>Viewing as: <span className="font-medium text-[var(--clr-text-primary)] capitalize">{userRole.replace('_', ' ')}</span></span>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8 max-w-4xl">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-[var(--clr-text-muted)] mb-6">
            <Home className="w-4 h-4" />
            <ChevronRight className="w-4 h-4" />
            <span>Documentation</span>
            <ChevronRight className="w-4 h-4" />
            <span className="text-[var(--clr-text-primary)]">
              {activeDoc === 'user-manual' && 'User Manual'}
              {activeDoc === 'quick-start' && 'Quick Start Guides'}
              {activeDoc === 'admin-guide' && 'Admin Configuration Guide'}
            </span>
          </div>

          {/* Content */}
          <article className="prose prose-slate max-w-none" data-testid="docs-content">
            {searchQuery && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                Showing results for "{searchQuery}"
              </div>
            )}
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {filteredContent || 'No documentation content available.'}
            </ReactMarkdown>
          </article>

          {/* Footer */}
          <footer className="mt-12 pt-6 border-t border-[var(--clr-border-light)]">
            <div className="flex items-center justify-between text-sm text-[var(--clr-text-muted)]">
              <p>Last updated: March 4, 2026</p>
              <a 
                href="/help-support" 
                className="text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1"
              >
                Need more help?
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default DocumentationPage;
